import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $templateDocuments } from "../document-list/template-documents.atom";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import type { MutationExecutorInput } from "./mutation-handle";
import { $lastQueryKeyForPage, pageKey } from "./page-last-status.atom";
import { $queryExecutorCore } from "./query-executor-core.atom";
import { registerQueryStateKeyForDocument } from "./query-state.atom";
import { $httpRequestPage } from "./request-page.atom";
import type { QueryResponse } from "./request-page.types";
import { mergeParams } from "./request-params";
import { requestPageKeyForDocsRoute } from "./requestPageKey";

const logger = new Logger("mutation-executor");

// =============================================================================
// Mutation Executor (Document-scoped)
// =============================================================================

/**
 * Write-only atomFamily that resolves page names to request specs and executes
 * them as mutations.
 *
 * Unlike $queryExecutor, this:
 * - Does not cache results in $queryState
 * - Does not deduplicate concurrent requests
 * - Returns QueryResponse directly (not QueryStateEntry)
 * - Skips template preview/SWR logic
 * - Rejects execution in template preview mode
 */
export const $mutationExecutor = atomFamily((documentId: string) => {
	const executorAtom = atom(
		null, // Write-only
		async (get, set, input: MutationExecutorInput): Promise<QueryResponse> => {
			const { pageName, params } = input;

			// Resolve page name to request spec
			const metadataKey = metadataKeyForDocsRoute(documentId);
			const metadata = get($documentMetadata(metadataKey));

			if (!metadata) {
				throw { code: "PAGE_NOT_LOADED", message: "Document metadata not loaded" };
			}

			// Template preview: never execute real mutations
			const isTemplate = metadata.type === "template";
			const isTemplatePreview = isTemplate && documentId in get($templateDocuments);
			if (isTemplatePreview) {
				throw {
					code: "UNKNOWN" as const,
					message: `Cannot execute mutations in template preview mode. Mutations are disabled for template previews.`,
				};
			}

			const pageMetadata = metadata.pagesMetadata.find(
				(p) => p.title === pageName && p.type === "request"
			);

			if (!pageMetadata) {
				throw {
					code: "PAGE_NOT_FOUND",
					message: `Request page "${pageName}" not found`,
				};
			}

			const pageId = pageMetadata.id;
			const requestPageKey = requestPageKeyForDocsRoute({ documentId, pageId });
			const requestPage = get($httpRequestPage(requestPageKey));

			if (!requestPage) {
				throw {
					code: "PAGE_NOT_LOADED",
					message: `Request page "${pageName}" not loaded`,
				};
			}

			const request = requestPage.request.request;
			const mergedParams = mergeParams(request, params);

			// Mutations still execute independently. The fresh key here is only
			// used to publish the latest execution into the same request-page state
			// channel that query() already drives.
			const key = `mutation:${documentId}:${pageId}:${crypto.randomUUID()}`;

			set($lastQueryKeyForPage(pageKey(documentId, pageId)), key);
			registerQueryStateKeyForDocument(get, set, documentId, key);

			logger.debug("Executing mutation", { pageName, documentId, key });

			const result = await set($queryExecutorCore, {
				key,
				request,
				params: mergedParams,
				documentId,
				pageId,
				pageName,
			});

			if (result.error) {
				throw result.error;
			}

			if (!result.data) {
				throw {
					code: "UNKNOWN" as const,
					message: `Mutation "${pageName}" completed without returning data`,
				};
			}

			return result.data;
		}
	);
	executorAtom.debugLabel = `mutationExecutor/${documentId}`;
	return executorAtom;
});
